<template>
  <app-modal @close="$emit('close')">
    <section class="bg-white shadow-lg max-w-sm w-screen mx-4 overflow-hidden">
      <header class="px-6 py-4 bg-grey-100 border-b flex">
        <icon icon="add-circle" class="icons-primary w-8 h-8 mr-2" />
        <h1 class="text-xl flex-1">
          invite new team member
        </h1>
      </header>
      <form novalidate class="p-6" @submit.prevent="$emit('team:invite', invite)">
        <p class="text-lg font-bold">
          you are inviting this member to {{ company.name | sentencecase }}.
        </p>
        <project-notice-item
          v-if="invitemodalnotice"
          class="mt-2"
          :notice="invitemodalnotice"
        />
        <div class="my-8 border focus-within:border-primary-500">
          <label
            for="inviteemail"
            class="block uppercase font-bold text-xs -mt-2 pl-4 text-grey-600"
            :class="errors.name ? 'text-negative-600' : 'text-grey-600'"
          >
            <span class="bg-white px-1 -ml-3">team member name</span>
          </label>
          <input
            id="invitename"
            v-model.trim="invite.name"
            type="text"
            required
            placeholder="jane doe"
            class="w-full focus:outline-none px-3 pt-2 pb-4 bg-transparent"
          />
          <span v-if="errors.name" class="text-negative-600 text-xs mb-1 mx-3">{{ errors.name[0] }}</span>
        </div>
        <div class="my-8 border focus-within:border-primary-500">
          <label
            for="invitejobtitle"
            class="block uppercase font-bold text-xs -mt-2 pl-4 text-grey-600"
            :class="errors.job_title ? 'text-negative-600' : 'text-grey-600'"
          >
            <span class="bg-white px-1 -ml-3">job title</span>
          </label>
          <input
            id="invitejobtitle"
            v-model.trim="invite.job_title"
            type="text"
            placeholder="e.g. project manager"
            class="w-full focus:outline-none px-3 pt-2 pb-4 bg-transparent"
          />
          <span v-if="errors.job_title" class="text-negative-600 text-xs mb-1 mx-3">{{ errors.job_title[0] }}</span>
        </div>
        <div class="my-8 border focus-within:border-primary-500">
          <label
            for="invitetelephone"
            class="block uppercase font-bold text-xs -mt-2 pl-4 text-grey-600"
            :class="errors.telephone ? 'text-negative-600' : 'text-grey-600'"
          >
            <span class="bg-white px-1 -ml-3">telephone number</span>
          </label>
          <input
            id="invitetelephone"
            v-model.trim="invite.telephone"
            type="tel"
            inputmode="tel"
            placeholder="e.g. +44 1234 567890"
            class="w-full focus:outline-none px-3 pt-2 pb-4 bg-transparent"
          />
          <span v-if="errors.telephone" class="text-negative-600 text-xs mb-1 mx-3">{{ errors.telephone[0] }}</span>
        </div>
        <div class="my-8 border focus-within:border-primary-500">
          <label
            for="inviteemail"
            class="block uppercase font-bold text-xs -mt-2 pl-4 text-grey-600"
            :class="errors.email ? 'text-negative-600' : 'text-grey-600'"
          >
            <span class="bg-white px-1 -ml-3">team member email</span>
          </label>
          <input
            id="inviteemail"
            v-model.trim="invite.email"
            type="email"
            required
            placeholder="e.g. a.name@company.com"
            class="w-full focus:outline-none px-3 pt-2 pb-4 bg-transparent"
          />
          <span v-if="errors.email" class="text-negative-600 text-xs mb-1 mx-3">{{ errors.email[0] }}</span>
        </div>
        <div class="mt-8 mb-2 border focus-within:border-primary-500">
          <label
            for="inviterole"
            class="block uppercase font-bold text-xs -mt-2 pl-4 text-grey-600"
            :class="errors.role ? 'text-negative-600' : 'text-grey-600'"
          >
            <span class="bg-white px-1 -ml-3">team member role</span>
          </label>
          <div class="flex items-center px-3 pt-1 pb-3 relative">
            <select
              id="inviterole"
              v-model="invite.role"
              required
              class="w-full appearance-none focus:outline-none bg-transparent z-10"
            >
              <option value="viewer" selected>
                viewer
              </option>
              <option value="editor">
                editor
              </option>
            </select>
            <icon
              icon="cheveron-selection"
              class="-ml-6 icons-grey w-6 h-6 z-0"
            />
          </div>
          <span v-if="errors.role" class="text-negative-600 text-xs mb-1 mx-3">{{ errors.role[0] }}</span>
        </div>
        <p class="text-xs italic mb-6">
          <template v-if="invite.role === 'editor'">
            editors have the ability to make changes to progress reports, submit
            claims, submit change requests and manage team members.
          </template>
          <template v-else>
            viewers can see all the information for a project but cannot make
            any changes.
          </template>
        </p>
        <div class="flex">
          <div class="w-1/2">
            <app-button type="submit">
              invite
            </app-button>
          </div>
          <div class="w-1/2 pl-2">
            <app-button type="button" colour="close" @click="$emit('close')">
              close
            </app-button>
          </div>
        </div>
      </form>
    </section>
  </app-modal>
</template>

<script>
export default {
  name: "TeamSectionInviteModal",
  props: {
    inviteModalNotice: {
      type: Object,
      required: false,
      default() {
        return {};
      }
    },
    company: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      invite: {
        name: "",
        email: "",
        job_title: "",
        telephone: "",
        role: "viewer"
      }
    };
  },
  computed: {
    validEmail() {
      return /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
        this.invite.email
      );
    },
    errors() {
      return this.inviteModalNotice.errors || {};
    }
  },
  beforeDestroy() {
    this.inviteReset();
  },
  methods: {
    inviteReset() {
      this.invite.name = "";
      this.invite.email = "";
      this.invite.job_title = "";
      this.invite.telephone = "";
      this.invite.role = "viewer";
    }
  }
};
</script>
